import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  isTeacherSide,
  type Lesson,
  LESSON_ENDED_PAGE_LIMIT_DEFAULT,
  LESSON_ENDED_PAGE_LIMIT_MAX,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { listErrorText } from "../../lib/list-error.ts";
import { lessonCountText } from "../../lib/plurals.ts";
import { LoadingRows } from "../../lib/loading.tsx";
import { useSession } from "../../lib/session.tsx";
import { guessTimeZone } from "../../lib/time-zone.ts";
import { mayPruneBoards } from "../../lib/view.ts";
import { AuthErrorBanner } from "../auth/AuthErrorBanner.tsx";
import { pruneBoardDatabases } from "../board/lesson-db.ts";
import { defaultStart, mondayOf } from "../schedule/common.ts";
import { CreateOccurrenceDialog } from "../schedule/CreateOccurrenceDialog.tsx";
import { P_DAY, P_WEEK } from "../schedule/lesson-link.ts";
import { appendLessonPage } from "./lesson-pages.ts";
import { LessonsPanel } from "./LessonsPanel.tsx";
import { mayCreateCall } from "../calls/call-row.ts";

/** Окно звонка с преподавателями лениво: его видят только держатели права. */
const CreateCallDialog = lazy(() =>
  import("../calls/CreateCallDialog.tsx").then((m) => ({ default: m.CreateCallDialog })),
);

/**
 * Уроки: раздел кабинета.
 *
 * ⚠️ Домашним этот раздел был до расписания, а с 15.08.2026 корень `/` уводит в
 * `/schedule` (разбор стоит у самой переадресации в `App.tsx`: пустое
 * расписание перестало быть тупиком, когда серию научились заводить мышью).
 * Здесь остался второй пункт сайдбара, список-вид на то же самое, и в нём же
 * живут уроки без даты, которых в сетке нет вовсе.
 *
 * Ролей у экрана нет, и это следует из модели: «преподаватель» и «ученик» это
 * позиция в конкретной паре, а не свойство человека. Один и тот же список
 * показывает и уроки, где я веду, и те, где я учусь; кнопки управления
 * появляются там, где сервер уже назвал меня преподавателем (`myRole`).
 *
 * ⚠️ **Урок заводится ЗДЕСЬ тем же окном, что и занятие расписания**, и это
 * решение владельца 17.09.2026: времени у создаваемого человеком урока больше
 * не бывает необязательным. До этого дня над списком стояла своя форма из трёх
 * полей, и заведённый ею урок не имел ни даты, ни времени вовсе, то есть в
 * расписании не появлялся никогда. Раздел при этом остался: это список-вид на
 * то же самое, и иногда он удобнее сетки.
 *
 * ⚠️ Отсюда же и единственный запрос экрана: список уроков, больше ничего.
 * Учеников и заготовки он грузил не ради списка, а ради полей той формы. Никуда
 * эти запросы не делись, их делает окно, и их там четыре (заготовки, ученики,
 * группы, серии), но платит за них тот, кто заводит занятие, и платит ПОСЛЕ
 * нажатия, а не каждый, кто открыл раздел.
 */
export function LessonsPage() {
  const { state } = useSession();
  /**
   * Заводить занятия может только преподаватель.
   *
   * ⚠️ Не украшение: `POST /v1/schedule/lessons` берёт преподавателя из сессии,
   * и своей паре ученик занятие не заведёт в принципе. Кнопка, ведущая в отказ,
   * хуже её отсутствия. Список уроков при этом общий на обе роли и остаётся на
   * месте.
   */
  const canCreate = state.status === "signed" && isTeacherSide(state.me);
  /**
   * Звонок с преподавателями: право есть, и это не просмотр кабинета (9.1
   * `docs/teacher-calls-design.md`).
   */
  const canCall = state.status === "signed" && mayCreateCall(state.me);
  const [calling, setCalling] = useState(false);
  const navigate = useNavigate();
  /**
   * Две половины списка, и грузятся они по-разному (шапка
   * `apps/api/src/repo/lesson-list.ts`): идущие и созданные целиком под
   * потолком, завершённые страницами по курсору.
   */
  const [active, setActive] = useState<{ lessons: Lesson[]; truncated: boolean } | null>(null);
  const [ended, setEnded] = useState<{
    lessons: Lesson[];
    nextCursor: string | null;
    /** Сколько строк у завершённых всего; `null` значит «сервер не прислал». */
    count: number | null;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreErr, setMoreErr] = useState<string | null>(null);
  /**
   * Поколение первой страницы: страница, запрошенная до перезагрузки списка,
   * не приклеивается к новой первой, её курсор снят с другого чтения.
   */
  const generation = useRef(0);
  /** Номер последней ушедшей догрузки: только она снимает занятость кнопки. */
  const moreRequest = useRef(0);
  /** Сколько завершённых строк на экране: по нему перезагрузка просит глубину. */
  const endedShown = useRef(0);
  endedShown.current = ended?.lessons.length ?? 0;
  /** Куда встанет новое занятие, либо `null`, когда окно закрыто. */
  const [creating, setCreating] = useState<{ date: string; time: string } | null>(null);
  /**
   * Заведена СЕРИЯ, и закрывать окно нужно переходом в расписание. Внутри
   * местная дата, на которую серия встала: без неё расписание откроется на
   * текущей неделе, а серию заводят на какую угодно.
   *
   * ⚠️ Обёртка, а не голая строка: `null` в дне означает «дата не приехала», и
   * отличать это от «серии не было» обязательно. Оба случая ведут в расписание,
   * но разными адресами.
   *
   * ⚠️ **Признак в `ref`, а не в состоянии, и это не про экономию отрисовки.**
   * Показывать его нечего: он ставится в одном обработчике окна и читается
   * через мгновение в соседнем обработчике того же окна, а на экране от него не
   * меняется ничего. Состояние здесь означало бы перерисовку ради значения,
   * которого никто не видит, в момент, когда экран всё равно уходит.
   */
  const madeSeries = useRef<{ day: string | null } | null>(null);

  /**
   * Зона смотрящего: подтверждённая из профиля, иначе догадка браузера.
   *
   * ⚠️ Правило то же самое, что на экране расписания (`SchedulePage.tsx`) и в
   * редакторе заготовки (`plans/CreateLessonHere.tsx`), и второго правила здесь
   * не заводится: окно у всех троих одно, и разойдись зоны, одно и то же
   * занятие вставало бы на разное время в зависимости от того, откуда его
   * завели. Догадка законна ровно потому, что ничего не
   * записывает: пока человек не подтвердил пояс, мы просто показываем время в
   * том, в котором он сидит.
   */
  const zone = useMemo(() => {
    const mine = state.status === "signed" ? state.me.timeZone : null;
    return mine ?? guessTimeZone();
  }, [state]);

  /**
   * Чей это кабинет по мнению ЭТОЙ вкладки: `null` в просмотре админом или пока
   * сессия не известна, тогда уборки нет вовсе.
   */
  const ownerId = state.status === "signed" && !state.me.viewer ? state.me.userId : null;

  const reload = useCallback(async () => {
    /*
      ⚠️ Глубина переживает перезагрузку: после действия в строке список
      перечитывается, и долистанные завершённые не прыгают обратно к двадцати.
      Первая страница просится размером с загруженное, но не больше MAX; строк
      при этом может приехать чуть меньше, потому что потолок считается в
      уроках, а занятие группы это несколько уроков в одной строке.
    */
    const limit = Math.min(
      Math.max(LESSON_ENDED_PAGE_LIMIT_DEFAULT, endedShown.current),
      LESSON_ENDED_PAGE_LIMIT_MAX,
    );
    try {
      const [act, end] = await Promise.all([
        api.listLessons({ part: "active" }),
        api.listLessons({ part: "ended", limit }),
      ]);
      /*
        Поколение растёт в момент ПРИМЕНЕНИЯ ответа, а не в начале запроса:
        догрузка, ушедшая, пока перезагрузка была в пути, иначе увидела бы
        старое поколение, дописалась к старому списку, и её затёрла бы новая
        первая страница, либо, пришедши позже, приклеилась бы к ней.
      */
      generation.current += 1;
      setActive({ lessons: act.lessons, truncated: act.truncated ?? false });
      setEnded({
        lessons: end.lessons,
        nextCursor: end.nextCursor ?? null,
        count: end.endedCount ?? null,
      });
      setErr(null);
      setMoreErr(null);
      setMoreBusy(false);

      /**
       * Уборка локальных баз доски. Список уроков это источник правды: сервер
       * уже применил правила доступа, значит всё, чего в ответе нет, на этом
       * устройстве держать не нужно. Не ждём и не показываем ошибок: это
       * гигиена хранилища, а не операция пользователя.
       *
       * Живёт именно здесь, а не на экране учеников: там списка уроков может
       * не быть вовсе, и уборка снесла бы базы живых уроков.
       *
       * ⚠️ Уроки уезжают ЦЕЛИКОМ, а не одними идентификаторами: у занятия группы
       * база названа сеансом, и различие «поля нет» против «сеанса нет» уборщик
       * обязан увидеть сам. Свернув список здесь до `l.id`, мы отняли бы у него
       * ровно тот признак, по которому он решает не трогать чужое.
       *
       * ⚠️ В просмотре кабинета админом уборки нет (12.6
       * `docs/admin-panel-design.md`): список здесь чужой, и по нему стёрлись
       * бы базы собственных уроков админа в этом браузере, включая
       * несинхронизированные.
       *
       * ⚠️ Состояния вкладки для этого МАЛО, и главный замок это свежий `getMe`.
       * Соседняя вкладка того же браузера могла открыть просмотр, и cookie уже
       * чужая, а эта вкладка по-прежнему считает себя кабинетом админа: список
       * выше тогда пришёл ЧУЖОЙ. Убираем, только если сервер прямо сейчас
       * называет того же человека и не просмотр (`mayPruneBoards`).
       *
       * ⚠️ **Уборка идёт ТОЛЬКО по полной половине `active`.** Упёршаяся в
       * потолок (`truncated`) выглядит как законный список, а стёрла бы базы
       * идущих и созданных уроков за его краем, включая брошенные `live` с
       * несинхронизированной работой. До 26.09.2026 так и было: потолок
       * молчал, и по обрезанному списку уборщик стирал всё за двухсотым уроком.
       *
       * ⚠️ **Завершённые за первой страницей стираются, и это проверено по
       * коду, а не принято на веру.** Непереданной правки у завершённого урока
       * быть не может в том смысле, что передать её уже некуда: статус `ended`
       * конечный (`markLive` переводит только из `created`, обратного пути нет
       * ни одного), а тикет доски на `ended` сервер не выдаёт (`lesson_ended`
       * у `requireLessonAccess` с нуждой `live`), то есть подключиться и
       * дослать офлайн-очередь y-indexeddb такой урок не может никогда, а
       * архив читает снимок с сервера, а не локальную базу. Появись однажды
       * путь назад из `ended` (переоткрытие занятия), это место обязано
       * перестать стирать базы завершённых.
       */
      if (ownerId !== null && !act.truncated) {
        const keep = [...act.lessons, ...end.lessons];
        void api
          .getMe()
          .then((fresh) => {
            if (mayPruneBoards(fresh, ownerId)) return pruneBoardDatabases(keep);
          })
          .catch(() => undefined);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      // Кнопка не остаётся в «Загрузка…» из-за упавшей перезагрузки
      setMoreBusy(false);
    }
  }, [ownerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openCreate = () => {
    setCreating(defaultStart(zone));
  };

  /** Следующая страница завершённых. Долистывание по кнопке: разбор у кнопки. */
  const loadMore = () => {
    const from = ended?.nextCursor ?? null;
    if (from === null || moreBusy) return;
    const mine = generation.current;
    const req = ++moreRequest.current;
    setMoreBusy(true);
    api
      .listLessons({ part: "ended", cursor: from })
      .then((page) => {
        if (generation.current !== mine) return;
        setEnded((prev) =>
          // Страница приклеивается только к тому хвосту, с которого её просили
          prev === null || prev.nextCursor !== from
            ? prev
            : {
                // Хвост занятия группы может приехать второй раз: разбор у склейки
                lessons: appendLessonPage(prev.lessons, page.lessons),
                nextCursor: page.nextCursor ?? null,
                count: prev.count,
              },
        );
        setMoreErr(null);
      })
      .catch((e: unknown) => {
        if (generation.current === mine) setMoreErr(listErrorText(e));
      })
      .finally(() => {
        // Занятость снимает последняя ушедшая догрузка, в том числе
        // отброшенная: иначе кнопка осталась бы в «Загрузка…» навсегда
        if (moreRequest.current === req) setMoreBusy(false);
      });
  };

  const lessons = active === null || ended === null ? null : [...active.lessons, ...ended.lessons];
  /*
    Число в шапке: половина `active` плюс ВСЕ завершённые строки, а не длина
    загруженного. У упёршейся в потолок половины числа нет вовсе, как в архиве:
    «200» над неполным списком было бы неправдой. У старого сервера без
    `endedCount` тоже нет: длина первой страницы это не число завершённых.
  */
  const total =
    active === null || ended === null || active.truncated || ended.count === null
      ? null
      : active.lessons.length + ended.count;

  return (
    <main className="home home--list">
      <AuthErrorBanner />

      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Уроки</h1>
          {total !== null && <span className="head__n">{lessonCountText(total)}</span>}
          {/* Единственный акцент экрана: занятия тут заводят, всё остальное
              это действия над уже созданными строками */}
          {(canCreate || canCall) && (
            <span className="head__act">
              {canCall && (
                <button type="button" className="btn" onClick={() => setCalling(true)}>
                  Звонок с преподавателями
                </button>
              )}
              {canCreate && (
                <button type="button" className="btn btn--primary" onClick={openCreate}>
                  Новое занятие
                </button>
              )}
            </span>
          )}
        </div>
        {/* То же место, что у архива (`ArchiveListPage.tsx`) */}
        {active?.truncated === true && <div className="head__sub">Показаны не все занятия</div>}
      </header>

      {err && <p className="err">{err}</p>}

      {lessons === null ? (
        <section className="card">
          <LoadingRows list="list--lessons" cells={7} narrow="panel" />
        </section>
      ) : (
        <LessonsPanel
          canCreate={canCreate}
          lessons={lessons}
          onCreate={openCreate}
          onChanged={() => void reload()}
          footer={
            <>
              {moreErr !== null && <p className="err">{moreErr}</p>}
              {/*
                Под «Завершёнными», последней группой списка. Кнопка, а не
                бесконечная прокрутка: на канале 128 кбит/с подгрузка «сама»
                означает подгрузку в момент, когда человек просто проскроллил мимо
              */}
              {ended?.nextCursor != null && (
                <p className="acts">
                  <button className="btn" disabled={moreBusy} onClick={loadMore}>
                    {moreBusy ? "Загрузка…" : "Показать ещё"}
                  </button>
                </p>
              )}
            </>
          }
        />
      )}

      {calling && (
        <Suspense fallback={null}>
          <CreateCallDialog onClose={() => setCalling(false)} />
        </Suspense>
      )}

      {creating !== null && (
        <CreateOccurrenceDialog
          date={creating.date}
          time={creating.time}
          zone={zone}
          /*
            ⚠️ **Заведя СЕРИЮ, уходим в расписание, и это не удобство, а
            единственная обратная связь.** Серия уроков не создаёт вовсе (их
            заводит занятие, когда до него дойдёт дело), поэтому список после неё
            не меняется ни строкой: человек нажимал и не видел ничего, а второе
            нажатие завело бы вторую серию, которую сервер принимает молча.
            Показать результат этот экран не может по устройству, а сетка
            расписания показывает его сразу. Расписание группы сюда же: уроков
            оно не создаёт, и видно его в той же сетке.

            ⚠️ **Переход стоит в ЗАКРЫТИИ, а не в `onCreated`, и это про
            постоянную ссылку ученика.** Между `onCreated` и закрытием окно
            выполняет `finish`, а там решается, закрываться ли вообще: заведя в
            этом же окне нового ученика, человек получает его постоянную ссылку,
            которая показывается ровно ОДИН раз (в базе только SHA-256), и пока
            её не забрали, окно остаётся открытым нарочно. Уйдя из `onCreated`,
            мы унесли бы эту ссылку с экрана вместе со всем окном, и
            перевыпускать её пришлось бы вручную.

            Разовое занятие приезжает уроком в аргумент (его окно отдаёт
            наружу), и для него всё остаётся как было: строка появляется в
            списке, с экрана никто никого не уводит.

            ⚠️ **Адрес несёт и неделю, и день, хотя показать надо один день.**
            `P_DAY` двигает только колонки ВНУТРИ показанной недели, а неделю
            расписание берёт из `P_WEEK`, иначе из сегодняшнего дня
            (`SchedulePage`). Серию заводят на любую дату, и с одним днём
            человек снова смотрел бы на неделю, в которой ничего не появилось.
            Ровно эту пару ставит и возврат из комнаты урока
            (`addressWithoutLink` в `schedule/lesson-link.ts`).

            ⚠️ Дня нет, значит уходим на расписание БЕЗ параметров, то есть на
            текущую неделю. Запасного значения тут не заводится: дата, с которой
            окно открывали, это не то, что человек в нём набрал, и подставить её
            значило бы уверенно открыть чужую неделю вместо честной сегодняшней.
          */
          onClose={() => {
            const made = madeSeries.current;
            if (made !== null) {
              madeSeries.current = null;
              const day = made.day;
              const p =
                day === null
                  ? null
                  : new URLSearchParams({ [P_WEEK]: mondayOf(day), [P_DAY]: day });
              navigate(p === null ? "/schedule" : `/schedule?${p.toString()}`);
              return;
            }
            setCreating(null);
          }}
          onCreated={async (lesson, day) => {
            if (lesson === undefined) {
              // Список перезагружать незачем: в нём ничего не появилось, и
              // экран мы сейчас покинем
              madeSeries.current = { day: day ?? null };
              return;
            }
            await reload();
          }}
        />
      )}
    </main>
  );
}
